import { formatMonthLabel, monthKeyBounds } from './month-key-utils';
import type {
  ContractsByCustomer,
  CustomerContracts,
  CustomerMrrByMonth,
  Movement,
  MrrMovementChartDataPoint,
} from './types';

const EPSILON = 0.005;

export function classifyMonthlyMovements(
  customerMrrByMonth: CustomerMrrByMonth,
  contractsByCustomer: ContractsByCustomer,
  monthKeys: string[],
): Map<string, MrrMovementChartDataPoint> {
  const result = new Map<string, MrrMovementChartDataPoint>();
  let prevEndingMrr = 0;

  for (let monthIndex = 0; monthIndex < monthKeys.length; monthIndex++) {
    const monthKey = monthKeys[monthIndex];
    const point = emptyPoint(formatMonthLabel(monthKey));
    point.beginningMrr = prevEndingMrr;
    const { start: periodStart, end: periodEnd } = monthKeyBounds(monthKey);

    for (const [customerId, monthlyMrr] of customerMrrByMonth) {
      const currMrr = monthlyMrr.get(monthKey) ?? 0;
      if (currMrr > 0) {
        point.endingMrr += currMrr;
      }

      const movement = classifyCustomerMovementForMonth({
        monthlyMrr,
        monthKeys,
        monthIndex,
        contracts: contractsByCustomer.get(customerId),
        periodStart,
        periodEnd,
      });

      if (movement) {
        applyMovementToPoint(point, movement);
      }
    }

    point.netNewMrr = sumMovements(point);
    result.set(monthKey, point);
    prevEndingMrr = point.endingMrr;
  }

  return result;
}

export function emptyPoint(label: string): MrrMovementChartDataPoint {
  return {
    label,
    new: 0,
    expansion: 0,
    reactivation: 0,
    contraction: 0,
    churn: 0,
    netNewMrr: 0,
    beginningMrr: 0,
    endingMrr: 0,
  };
}

type ClassifyForMonthInput = {
  monthlyMrr: Map<string, number>;
  monthKeys: string[];
  monthIndex: number;
  contracts: CustomerContracts | undefined;
  periodStart: Date;
  periodEnd: Date;
};

function classifyCustomerMovementForMonth({
  monthlyMrr,
  monthKeys,
  monthIndex,
  contracts,
  periodStart,
  periodEnd,
}: ClassifyForMonthInput): Movement | null {
  const currMrr = monthlyMrr.get(monthKeys[monthIndex]) ?? 0;
  const priorMrr = monthIndex > 0 ? (monthlyMrr.get(monthKeys[monthIndex - 1]) ?? 0) : 0;
  const hadPriorMrr = customerHadPriorMrr(monthlyMrr, monthKeys, monthIndex);

  return classifyCustomerMovement({
    currMrr,
    priorMrr,
    hadPriorMrr,
    contracts,
    periodStart,
    periodEnd,
  });
}

type ClassifyInput = {
  currMrr: number;
  priorMrr: number;
  hadPriorMrr: boolean;
  contracts: CustomerContracts | undefined;
  periodStart: Date;
  periodEnd: Date;
};

export function classifyCustomerMovement({
  currMrr,
  priorMrr,
  hadPriorMrr,
  contracts,
  periodStart,
  periodEnd,
}: ClassifyInput): Movement | null {
  if (currMrr <= EPSILON && priorMrr <= EPSILON) {
    return null;
  }

  if (priorMrr <= EPSILON && currMrr > EPSILON) {
    return classifyAppearance({ currMrr, hadPriorMrr, contracts, periodStart, periodEnd });
  }

  if (currMrr <= EPSILON && priorMrr > EPSILON) {
    return classifyDisappearance(priorMrr);
  }

  if (currMrr > priorMrr + EPSILON) {
    return { type: 'expansion', amount: currMrr - priorMrr };
  }

  if (currMrr < priorMrr - EPSILON) {
    return { type: 'contraction', amount: currMrr - priorMrr };
  }

  return null;
}

type AppearanceInput = {
  currMrr: number;
  hadPriorMrr: boolean;
  contracts: CustomerContracts | undefined;
  periodStart: Date;
  periodEnd: Date;
};

function classifyAppearance({
  currMrr,
  hadPriorMrr,
  contracts,
  periodStart,
  periodEnd,
}: AppearanceInput): Movement {
  if (hadPriorMrr) {
    return { type: 'reactivation', amount: currMrr };
  }

  // First-time appearance — spec wants contract_start_date in period, but a
  // customer whose first billed MRR lands after the contract started is still
  // "New" (there's no better bucket for it). So we always classify as New here.
  void contracts;
  void periodStart;
  void periodEnd;
  return { type: 'new', amount: currMrr };
}

function classifyDisappearance(priorMrr: number): Movement {
  return { type: 'churn', amount: -priorMrr };
}

function customerHadPriorMrr(
  monthlyMrr: Map<string, number>,
  monthKeys: string[],
  currentIndex: number,
): boolean {
  for (let i = 0; i < currentIndex; i++) {
    if ((monthlyMrr.get(monthKeys[i]) ?? 0) > EPSILON) {
      return true;
    }
  }
  return false;
}


function applyMovementToPoint(
  point: MrrMovementChartDataPoint,
  movement: Movement,
): void {
  switch (movement.type) {
    case 'new':
      point.new += movement.amount;
      return;
    case 'reactivation':
      point.reactivation += movement.amount;
      return;
    case 'expansion':
      point.expansion += movement.amount;
      return;
    case 'contraction':
      point.contraction += -Math.abs(movement.amount);
      return;
    case 'churn':
      point.churn += -Math.abs(movement.amount);
      return;
  }
}

function sumMovements(point: MrrMovementChartDataPoint): number {
  return point.new + point.expansion + point.reactivation + point.contraction + point.churn;
}
